import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    CustomerWorkoutSetCreatUpdate,
    resetCustomerWorkoutSetFormValue,
    resetSetItem,
    resetWorkoutSetItems,
    SelectableExercise,
    setContext,
    setCustomerWorkoutSetFormValue,
    setSelectableExercises,
    setSelectedExercise,
    setSelectedGroup,
    setSetItem,
    setWorkoutSetItems,
} from '../state/customer.slice';
import { CustomerWorkoutCreateSetFormData } from '../beans/customer.workout.set.create.form.data';
import { IntlContext } from 'react-intl';
import Select from 'react-select';
import { reactSelectStyles, reactSelectComponents } from 'src/features/_shared/styles/react-select.styles';
import { ContextEnum } from 'src/_shared/enums/context.enum';
import { ExerciseManager } from 'src/_shared/managers/exercise.manager';
import { FormManager } from 'src/_shared/managers/form.manager';
import { QueueManager, QueueKeyType } from 'src/_shared/managers/queue.manager';
import { WorkoutSessionSetManager } from 'src/_shared/managers/workout.session.set.manager';
import { store } from 'src/_shared/store/configure';
import { useStoreSelector } from 'src/_shared/store/hook';
import { FormHelperUtil } from 'src/_shared/utils/form.helper.util';
import {
    User,
    ProgramSession,
    ProgramSessionSet,
    SetCreateRequest,
    ProgramSessionSetCreateRequest,
    DashboardSessionSummary,
    DashboardSessionSummaryMuscleGroupSet,
    DashboardSessionSummaryExercise,
    DashboardSessionExerciseSummaryRequest,
    DashboardExerciseSummary,
    SetTransitByExerciseRequest,
    ProgramSessionSetStatusEnum,
} from 'src/api/entities';
import { TextField } from 'src/features/_shared/components/TextField';
import { muscleGroups, SelectableExerciseGroup } from 'src/features/exercise/utils/muscleGroups';
import { setSessionSummary, setWorkoutSessionSetItems } from 'src/features/workout/store/workout.session.slice';
import { Button } from 'src/features/_shared/components/Button/button.component';
import { DashboardManager } from 'src/_shared/managers/dashboard.manager';
import { setDashboardSessionExerciseSummary } from '../state/customer.analysis.slice';
import { PageTransition } from 'src/features/_shared/components/PageTransition/page.transition.component';

const CollapsibleSection: React.FC<{ isOpen: boolean; children: React.ReactNode }> = ({ isOpen, children }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>(0);

    useEffect(() => {
        if (contentRef.current) {
            setHeight(contentRef.current.scrollHeight);
        }
    });

    return (
        <div
            style={{
                maxHeight: isOpen ? height : 0,
                overflow: 'hidden',
                transition: 'max-height 0.3s ease',
            }}>
            <div ref={contentRef}>{children}</div>
        </div>
    );
};

export const CustomerWorkoutSessionSetsView: React.FC = () => {
    const queueManager = QueueManager.getInstance();
    const exerciseManager = ExerciseManager.getInstance();
    const workoutSessionSetManager = WorkoutSessionSetManager.getInstance();
    const dashboardManager = DashboardManager.getInstance();

    const sessionSummaryItem: DashboardSessionSummary = useStoreSelector(state => state.reducer.workoutSessionSlice.sessionSummary);
    const customerItem: User = useStoreSelector(state => state.reducer.customerSlice.customer);
    const workoutSessionItem: ProgramSession = useStoreSelector(state => state.reducer.workoutSessionSlice.workoutSession);
    const setCreateUpdate: CustomerWorkoutSetCreatUpdate = useStoreSelector(state => state.reducer.customerSlice.setCreateUpdate);
    const selectableExercises: SelectableExercise[] = useStoreSelector(state => state.reducer.customerSlice.selectableExercises);
    const programSessionSets: ProgramSessionSet[] = useStoreSelector(state => state.reducer.workoutSessionSlice.workoutSessionSets);
    const setItem: ProgramSessionSet = useStoreSelector(state => state.reducer.customerSlice.setItem);
    const dashboardSessionSummaryExercise: DashboardExerciseSummary[] = useStoreSelector(state => state.reducer.customerAnalysisSlice.dashboardSessionExerciseSummary);

    const searchSetItems = async () => {
        if (workoutSessionItem != undefined) {
            const sets = (await workoutSessionSetManager.search(workoutSessionItem.id!)).data;
            if (sets != undefined) {
                store.dispatch(setWorkoutSessionSetItems({ sets }));
                const request: DashboardSessionExerciseSummaryRequest = {
                    authorID: customerItem.id!,
                    sessionID: workoutSessionItem.id,
                };
                const dashboardSessionExerciseSummaryResponse = await dashboardManager.getDashboardSessionExerciseSummary(request);
                store.dispatch(setDashboardSessionExerciseSummary({ dashboardSessionExerciseSummaryResponse }));
            }
        }
    };

    const getSessionSummary = async () => {
        if (workoutSessionItem != undefined) {
            const response = await dashboardManager.getSessionSummary(workoutSessionItem.id!);
            store.dispatch(setSessionSummary({ response }));
        }
    };

    const getItems = async () => {
        console.log(sessionSummaryItem);
        const value = await exerciseManager.search(undefined, muscleGroups[0].value);
        store.dispatch(setSelectableExercises({ value }));
        await searchSetItems();
        await getSessionSummary();
    };

    useEffect(() => {
        getItems();
        setSetsCounter(1);
    }, []);

    const intl = useContext(IntlContext);
    const getMessageByFieldAndError = FormHelperUtil.getFormFieldErrorTranslator('wizard.general.data', intl as any);

    const createWorkoutSet = async (request: SetCreateRequest) => {
        await workoutSessionSetManager.create(workoutSessionItem.id!, request), await searchSetItems();
    };

    const handleCreateWorkoutSet = () => {
        for (let i = 0; i < setsCounter; i++) {
            const workoutSetCreating: ProgramSessionSetCreateRequest = {
                sessionID: workoutSessionItem.id!,
                authorId: customerItem.id!,
                authorName: customerItem.username,
                data: {
                    details: {
                        date: workoutSessionItem.sessionDate,
                        exercise: {
                            group: setCreateUpdate.exerciseGroup,
                            name: setCreateUpdate.exerciseName,
                            uuid: setCreateUpdate.exerciseUuid,
                        },
                        reps: setCreateUpdate.reps,
                        rest: setCreateUpdate.rest,
                        weight: setCreateUpdate.weight,
                    },
                },
            };
            queueManager.queue(QueueKeyType.DEFAULT, createWorkoutSet(workoutSetCreating));
        }

        queueManager.queue(QueueKeyType.DEFAULT, getSessionSummary());
        store.dispatch(resetCustomerWorkoutSetFormValue());
        setCustomerWorkoutSetCreatUpdateState({
            exerciseGroup: '',
            exerciseName: '',
        });
    };

    const handleSelectGroup = async (group: SelectableExerciseGroup) => {
        const value = await queueManager.queue(QueueKeyType.DEFAULT, exerciseManager.search(undefined, undefined, group.value));
        store.dispatch(setSelectedGroup({ group }));
        store.dispatch(setSelectableExercises({ value }));
        setCustomerWorkoutSetCreatUpdateState({
            exerciseGroup: group.value,
        });
        workoutSetCreateFormData.reset({
            groupExercises: null,
        });
    };

    const handleSelectExercise = (value: SelectableExercise) => {
        store.dispatch(setSelectedExercise({ value }));
        setCustomerWorkoutSetCreatUpdateState(prevState => ({
            ...prevState,
            exerciseName: value.value,
            exerciseGroup: customerWorkoutSetCreateState.exerciseGroup,
        }));
    };

    const handleDelete = async (uuid: string) => {
        await queueManager.queue(QueueKeyType.DEFAULT, workoutSessionSetManager.delete(uuid));
        await getItems();
        queueManager.queue(QueueKeyType.DEFAULT, getSessionSummary());
    };
    const handleDuplicate = async (duplicatingSet: ProgramSessionSet) => {
        const workoutSetCreating: ProgramSessionSetCreateRequest = {
            sessionID: duplicatingSet.sessionID!,
            authorId: duplicatingSet.authorId,
            authorName: duplicatingSet.authorName,
            status: ProgramSessionSetStatusEnum.Done,
            data: {
                details: {
                    date: duplicatingSet.data!.details!.date!,
                    exercise: {
                        group: duplicatingSet.exerciseGroup,
                        name: duplicatingSet.exerciseName,
                        uuid: duplicatingSet.exerciseUuid,
                    },
                    reps: duplicatingSet.reps,
                    rest: duplicatingSet.rest,
                    weight: duplicatingSet.weight,
                },
            },
        };
        queueManager.queue(QueueKeyType.DEFAULT, createWorkoutSet(workoutSetCreating));
        await getItems();
        queueManager.queue(QueueKeyType.DEFAULT, getSessionSummary());
    };

    const handleStartEdit = (value: ProgramSessionSet) => {
        store.dispatch(setContext({ value: ContextEnum.UPDATE }));
        store.dispatch(setSetItem({ value }));
        store.dispatch(setCustomerWorkoutSetFormValue({ key: 'exerciseName', value: value.exerciseName! }));
        store.dispatch(setCustomerWorkoutSetFormValue({ key: 'exerciseGroup', value: value.exerciseGroup! }));
        store.dispatch(setCustomerWorkoutSetFormValue({ key: 'exerciseUuid', value: value.exerciseUuid! }));
        store.dispatch(setCustomerWorkoutSetFormValue({ key: 'uuid', value: value.uuid! }));
        store.dispatch(setCustomerWorkoutSetFormValue({ key: 'reps', value: value.reps! }));
        store.dispatch(setCustomerWorkoutSetFormValue({ key: 'weight', value: value.weight! }));
        setCustomerWorkoutSetUpdateState({
            exerciseGroup: value.exerciseGroup,
            exerciseName: value.exerciseName,
        });
        workoutSetCreateFormData.setValue('editWeight', value.weight);
        workoutSetCreateFormData.setValue('editReps', value.reps);
    };

    const handleCancelUpdate = () => {
        store.dispatch(setContext({ value: ContextEnum.CREATE }));
        store.dispatch(resetSetItem());
        setCustomerWorkoutSetUpdateState({
            exerciseGroup: '',
            exerciseName: '',
        });
        workoutSetCreateFormData.setValue('editWeight', '');
        workoutSetCreateFormData.setValue('editReps', '');
        workoutSetCreateFormData.resetField('weight');
        workoutSetCreateFormData.resetField('reps');
        store.dispatch(resetCustomerWorkoutSetFormValue());
    };

    const handleOnBlur = (key: any, value: string) => {
        store.dispatch(setCustomerWorkoutSetFormValue({ key, value }));
    };

    const handleSaveUpdate = async () => {
        store.dispatch(setContext({ value: ContextEnum.CREATE }));
        await queueManager.queue(
            QueueKeyType.DEFAULT,
            workoutSessionSetManager.edit(setItem, {
                ...setItem,
                weight: workoutSetCreateFormData.getValues('editWeight'),
                reps: workoutSetCreateFormData.getValues('editReps'),
            }),
        );
        store.dispatch(setContext({ value: ContextEnum.CREATE }));
        store.dispatch(resetSetItem());
        store.dispatch(resetCustomerWorkoutSetFormValue());
        setCustomerWorkoutSetUpdateState({
            exerciseGroup: '',
            exerciseName: '',
        });
        workoutSetCreateFormData.setValue('editWeight', '');
        workoutSetCreateFormData.setValue('editReps', '');
        workoutSetCreateFormData.resetField('weight');
        workoutSetCreateFormData.resetField('reps');
        getItems();
    };

    const workoutSetCreateFormData = FormManager.getInstance().buildFormProxy<CustomerWorkoutCreateSetFormData>(
        CustomerWorkoutCreateSetFormData,
        handleCreateWorkoutSet,
        {
            exerciseName: setCreateUpdate.exerciseName,
            exerciseGroup: setCreateUpdate.exerciseGroup,
            exerciseUuid: setCreateUpdate.exerciseUuid,
            uuid: setCreateUpdate.uuid,
            weight: setCreateUpdate.weight,
            reps: setCreateUpdate.reps,
        },
        'onChange',
    );

    const handleClearWorkoutSet = () => {
        store.dispatch(resetCustomerWorkoutSetFormValue());
        setCustomerWorkoutSetCreatUpdateState({
            exerciseGroup: '',
            exerciseUuid: '',
            exerciseName: '',
        });
        workoutSetCreateFormData.resetField('weight');
        workoutSetCreateFormData.resetField('reps');
        store.dispatch(resetCustomerWorkoutSetFormValue());
    };

    const [customerWorkoutSetCreateState, setCustomerWorkoutSetCreatUpdateState] = useState<CustomerWorkoutSetCreatUpdate>(setCreateUpdate);
    const [customerWorkoutSetUpdateState, setCustomerWorkoutSetUpdateState] = useState<CustomerWorkoutSetCreatUpdate>(setCreateUpdate);
    const [setsCounter, setSetsCounter] = useState<number>(0);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const statPill = (label: string, value: string | number | undefined) =>
        value ? (
            <div
                className='d-flex justify-content-center gap-2 px-3 py-2 align-items-center text-center flex-shrink-0'
                style={{ backgroundColor: '#f5f5f5', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '0.75rem' }}>
                <p className='m-0' style={{ color: '#888', fontSize: '0.8rem' }}>
                    {label}
                </p>
                <p className='m-0 fw-bold' style={{ color: '#333', fontSize: '0.8rem' }}>
                    {value}
                </p>
            </div>
        ) : null;

    return (
        <PageTransition className='mt-4 px-3 px-md-4'>
            {/* ── Page Header ──────────────────────────────────────────── */}
            <div
                className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-4 anim-slide-up'
                style={{
                    background: 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
                    borderRadius: 12,
                    border: '1px solid rgba(0,0,0,0.06)',
                }}>
                <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                        <i className='bi bi-calendar2-check' style={{ fontSize: '1.4rem', color: '#ff4949' }}></i>
                        <h4 className='mb-0 fw-bold' style={{ color: '#333' }}>
                            {workoutSessionItem.sessionName}
                        </h4>
                    </div>
                    <p className='mb-0 mt-1' style={{ color: '#666', fontSize: '0.9rem' }}>
                        {programSessionSets.length > 0 ? (
                            <>
                                <span className='badge rounded-pill me-2' style={{ backgroundColor: '#ff4949', color: '#fff', fontSize: '0.8rem' }}>
                                    {programSessionSets.length}
                                </span>
                                serie inserite
                            </>
                        ) : (
                            'Aggiungi serie alla sessione'
                        )}
                    </p>
                </div>
            </div>

            {/* ── Add Set Panel ─────────────────────────────────────────── */}
            <div
                className='mb-4 p-3 anim-slide-up'
                style={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderLeft: '3px solid #ff4949',
                }}>
                <div className='d-flex align-items-center gap-2 mb-3'>
                    <i className='bi bi-plus-circle-fill' style={{ fontSize: '0.95rem', color: '#ff4949' }}></i>
                    <span className='fw-semibold' style={{ color: '#333', fontSize: '0.9rem' }}>
                        Nuova serie
                    </span>
                </div>
                {/* Row 1: Selects */}
                <div className='row g-3 mb-3'>
                    <div className='col-12 col-md-6'>
                        <Select
                            styles={{
                                ...reactSelectStyles,
                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            components={reactSelectComponents}
                            options={muscleGroups.sort((a, b) => b.label.localeCompare(a.label)).reverse()}
                            placeholder='Seleziona un gruppo...'
                            onChange={(choice: SelectableExerciseGroup) => {
                                workoutSetCreateFormData.setValue('exerciseGroup.value', choice);
                                handleSelectGroup(choice);
                            }}
                            value={!customerWorkoutSetCreateState.exerciseGroup ? null : muscleGroups.find(obj => obj.value === customerWorkoutSetCreateState.exerciseGroup)}
                        />
                    </div>
                    <div className='col-12 col-md-6'>
                        <Select
                            styles={{
                                ...reactSelectStyles,
                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            components={reactSelectComponents}
                            options={selectableExercises}
                            placeholder='Seleziona un esercizio...'
                            onChange={(choice: SelectableExercise) => {
                                handleSelectExercise(choice!);
                            }}
                            value={!customerWorkoutSetCreateState.exerciseName ? null : selectableExercises.find(obj => obj.value === customerWorkoutSetCreateState.exerciseName)}
                        />
                    </div>
                </div>
                {/* Row 2: Inputs + Buttons */}
                <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3'>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            type='number'
                            value={setsCounter}
                            id='sets'
                            placeholder='Sets'
                            onChange={e => {
                                setSetsCounter(Number(e.target.value));
                                console.log(setsCounter);
                            }}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            type='text'
                            placeholder='Reps'
                            fullWidth
                            id='reps'
                            {...workoutSetCreateFormData.register('reps')}
                            error={workoutSetCreateFormData.errors.reps}
                            errorMessage={workoutSetCreateFormData.errors.reps && 'Campo obbligatorio'}
                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlur(e.target.name, e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            type='text'
                            placeholder='Weight'
                            fullWidth
                            id='weight'
                            {...workoutSetCreateFormData.register('weight')}
                            error={workoutSetCreateFormData.errors.weight}
                            errorMessage={workoutSetCreateFormData.errors.weight && 'Campo obbligatorio'}
                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlur(e.target.name, e.target.value)}
                        />
                    </div>
                    <div className='d-flex gap-2'>
                        <Button
                            identifier='Aggiungi'
                            icon={<i className='bi bi-plus-lg' style={{ fontSize: '0.9rem', color: 'white' }}></i>}
                            onClick={() => handleCreateWorkoutSet()}
                            variant='primary'
                        />
                        <Button
                            identifier='Pulisci'
                            icon={<i className='bi bi-x-circle' style={{ fontSize: '0.9rem' }}></i>}
                            onClick={() => handleClearWorkoutSet()}
                            variant='secondary'
                        />
                    </div>
                </div>
            </div>

            {/* ── Summary Pills ────────────────────────────────────────── */}
            {sessionSummaryItem && sessionSummaryItem.muscleGroupSets && sessionSummaryItem.muscleGroupSets.length > 0 && (
                <>
                    <div className='d-flex flex-wrap gap-2 mb-3 anim-slide-up'>
                        {statPill('Total', sessionSummaryItem.total)}
                        {sessionSummaryItem.muscleGroupSets.map((summaryItem: DashboardSessionSummaryMuscleGroupSet, i: number) => (
                            <React.Fragment key={i}>{statPill(summaryItem.exerciseGroup!, summaryItem.sets)}</React.Fragment>
                        ))}
                    </div>
                    {sessionSummaryItem.exerciseSets && sessionSummaryItem.exerciseSets.length > 0 && (
                        <div className='d-flex flex-wrap gap-2 mb-4 anim-slide-up'>
                            {sessionSummaryItem.exerciseSets.map((summaryExerciseItem: DashboardSessionSummaryExercise, i: number) => (
                                <React.Fragment key={i}>{statPill(summaryExerciseItem.exerciseName!, summaryExerciseItem.sets)}</React.Fragment>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ── Exercise Set List ────────────────────────────────────── */}
            {programSessionSets.length > 0 && (
                <div className='d-flex flex-column gap-4 mb-4'>
                    {Object.entries(
                        programSessionSets.reduce((acc, set) => {
                            if (!acc[set.exerciseName!]) {
                                acc[set.exerciseName!] = [];
                            }
                            acc[set.exerciseName!].push(set);
                            return acc;
                        }, {} as Record<string, ProgramSessionSet[]>),
                    ).map(([exerciseName, sets], groupIndex) => {
                        const exerciseSummary = dashboardSessionSummaryExercise?.find(sum => sum.name == exerciseName);
                        const isOpen = !!openGroups[exerciseName];
                        const toggleGroup = () => setOpenGroups(prev => ({ ...prev, [exerciseName]: !prev[exerciseName] }));

                        return (
                            <div
                                key={exerciseName}
                                className='app-card anim-stagger'
                                style={
                                    {
                                        '--anim-index': groupIndex,
                                        borderLeft: '3px solid #ff4949',
                                        padding: 0,
                                        overflow: 'hidden',
                                    } as React.CSSProperties
                                }>
                                {/* Exercise group header — clickable */}
                                <div className='d-flex align-items-center justify-content-between p-3' style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleGroup}>
                                    <div className='d-flex align-items-center gap-2'>
                                        <i className='bi bi-activity' style={{ fontSize: '1rem', color: '#ff4949' }}></i>
                                        <h6 className='mb-0 fw-bold' style={{ color: '#333' }}>
                                            {exerciseName}
                                        </h6>
                                        <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.75rem' }}>
                                            {sets.length}
                                        </span>
                                    </div>
                                    <i
                                        className='bi bi-chevron-down'
                                        style={{
                                            fontSize: '1rem',
                                            color: '#888',
                                            transition: 'transform 0.3s ease',
                                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}></i>
                                </div>

                                {/* Collapsible content */}
                                <CollapsibleSection isOpen={isOpen}>
                                    <div className='px-3 pb-3'>
                                        {/* Exercise stats pills */}
                                        {exerciseSummary && (
                                            <div className='d-flex flex-wrap gap-2 mb-3'>
                                                {statPill('AVG weight', exerciseSummary.averageWeight)}
                                                {statPill('MAX weight', exerciseSummary.maxWeight)}
                                                {statPill('MIN weight', exerciseSummary.minWeight)}
                                                {statPill('AVG reps', exerciseSummary.averageReps)}
                                                {statPill('MAX reps', exerciseSummary.maxReps)}
                                                {statPill('MIN reps', exerciseSummary.minReps)}
                                                {statPill('1RM', exerciseSummary.oneRm)}
                                                {statPill('95%', exerciseSummary.oneRm95)}
                                                {statPill('90%', exerciseSummary.oneRm90)}
                                                {statPill('85%', exerciseSummary.oneRm85)}
                                                {statPill('80%', exerciseSummary.oneRm80)}
                                                {statPill('75%', exerciseSummary.oneRm75)}
                                                {statPill('70%', exerciseSummary.oneRm70)}
                                            </div>
                                        )}

                                        {/* Last session info */}
                                        {exerciseSummary?.lastSessionDetails && (
                                            <div className='d-flex flex-wrap gap-2 mb-3'>
                                                <div
                                                    className='d-flex justify-content-center gap-2 px-3 py-2 align-items-center text-center flex-shrink-0'
                                                    style={{ backgroundColor: '#f5f5f5', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '0.75rem' }}>
                                                    <p className='m-0' style={{ color: '#888', fontSize: '0.8rem' }}>
                                                        Last session
                                                    </p>
                                                    <p className='m-0 fw-bold' style={{ color: '#333', fontSize: '0.8rem' }}>
                                                        {exerciseSummary.lastSessionDetails.date}
                                                    </p>
                                                    <p className='m-0 fw-bold' style={{ color: '#333', fontSize: '0.8rem' }}>
                                                        {exerciseSummary.lastSessionDetails.weight}
                                                    </p>
                                                    <p className='m-0 fw-bold' style={{ color: '#333', fontSize: '0.8rem' }}>
                                                        {exerciseSummary.lastSessionDetails.reps}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Set rows */}
                                        <div className='d-flex flex-column gap-2'>
                                            {sets.map((currentSet: ProgramSessionSet) => (
                                                <div key={currentSet.uuid}>
                                                    {currentSet.id !== setItem.id ? (
                                                        <div
                                                            className='d-flex flex-row align-items-center justify-content-between p-2'
                                                            style={{ backgroundColor: '#fafafa', borderRadius: 8, border: '1px solid rgba(0,0,0,0.04)' }}>
                                                            <div className='d-flex gap-4'>
                                                                <span style={{ color: '#666', fontSize: '0.85rem' }}>{currentSet.exerciseName}</span>
                                                                <span className='fw-semibold' style={{ fontSize: '0.85rem' }}>
                                                                    {currentSet.weight} kg
                                                                </span>
                                                                <span className='fw-semibold' style={{ fontSize: '0.85rem' }}>
                                                                    {currentSet.reps} reps
                                                                </span>
                                                            </div>
                                                            <div className='d-flex gap-2'>
                                                                <Button identifier='Duplica' onClick={() => handleDuplicate(currentSet)} variant='primary' />
                                                                <Button identifier='Modifica' onClick={() => handleStartEdit(currentSet)} variant='primary' />
                                                                <Button identifier='Elimina' onClick={() => handleDelete(currentSet.uuid!)} variant='secondary' />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className='row align-items-center p-2 g-2'
                                                            style={{ backgroundColor: '#fff8f0', borderRadius: 8, border: '1px solid #ffe0b2' }}>
                                                            <div className='col-6 col-md-2'>
                                                                <TextField
                                                                    type='text'
                                                                    placeholder='Weight'
                                                                    fullWidth
                                                                    id='editWeight'
                                                                    {...workoutSetCreateFormData.register('editWeight')}
                                                                    error={workoutSetCreateFormData.errors.weight}
                                                                    errorMessage={
                                                                        workoutSetCreateFormData.errors.weight &&
                                                                        getMessageByFieldAndError('editWeight', workoutSetCreateFormData.errors)
                                                                    }
                                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlur(e.target.name, e.target.value)}
                                                                />
                                                            </div>
                                                            <div className='col-6 col-md-2'>
                                                                <TextField
                                                                    type='text'
                                                                    placeholder='Reps'
                                                                    fullWidth
                                                                    id='editReps'
                                                                    {...workoutSetCreateFormData.register('editReps')}
                                                                    error={workoutSetCreateFormData.errors.reps}
                                                                    errorMessage={
                                                                        workoutSetCreateFormData.errors.reps &&
                                                                        getMessageByFieldAndError('editReps', workoutSetCreateFormData.errors)
                                                                    }
                                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlur(e.target.name, e.target.value)}
                                                                />
                                                            </div>
                                                            <div className='col-auto d-flex gap-2'>
                                                                <Button identifier='Salva' onClick={() => handleSaveUpdate()} variant='primary' />
                                                                <Button identifier='Annulla' onClick={() => handleCancelUpdate()} variant='secondary' />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </CollapsibleSection>
                            </div>
                        );
                    })}
                </div>
            )}
        </PageTransition>
    );
};
